import React, { useState } from 'react';
import { Collaborator, SplitData, SplitResponse, SplitCollaboratorInput } from '../types';
import { createSplit, downloadSplitPDF } from '../services/api';
import { useSplitValidation } from '../hooks/useSplitValidation';
import { useToast } from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import ProUpgrade from './ProUpgrade';
import CollaboratorSelector from './CollaboratorSelector';
import { PrimaryButton, SecondaryButton } from './buttons';

const SplitCalculator: React.FC = () => {
  const [songTitle, setSongTitle] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('');
  const [songCreationDate, setSongCreationDate] = useState('');
  const [alternateTitles, setAlternateTitles] = useState<string[]>([]);
  const [newAlternateTitle, setNewAlternateTitle] = useState('');
  const [collaborators, setCollaborators] = useState<SplitCollaboratorInput[]>([
    { name: '', role: 'composer', share: 0, email: '', ipiNumber: '', pro: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SplitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [showProUpgrade, setShowProUpgrade] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // NEW STATE for enhanced features
  const [sendInvites, setSendInvites] = useState(false);
  const [agreeOnBehalf, setAgreeOnBehalf] = useState(false);
  
  // State for create artist account modal
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  
  // Get account context - selectedAccount is set via header account picker
  const { accounts, selectedAccount, isManager, createAccount, refreshAccounts } = useAccount();
  
  // Filter to only artist accounts (works can only be assigned to artist accounts)
  const artistAccounts = accounts.filter(acc => acc.type === 'artist');

  // Helper to convert minutes/seconds to total seconds
  const getDurationInSeconds = (): number | undefined => {
    const mins = parseInt(durationMinutes) || 0;
    const secs = parseInt(durationSeconds) || 0;
    const total = mins * 60 + secs;
    return total > 0 ? total : undefined;
  };

  // Helper to add alternate title
  const addAlternateTitle = () => {
    const trimmed = newAlternateTitle.trim();
    if (trimmed && !alternateTitles.includes(trimmed) && alternateTitles.length < 10) {
      setAlternateTitles([...alternateTitles, trimmed]);
      setNewAlternateTitle('');
    }
  };

  // Helper to remove alternate title
  const removeAlternateTitle = (index: number) => {
    setAlternateTitles(alternateTitles.filter((_, i) => i !== index));
  };

  const { showToast, ToastComponent } = useToast();
  const { isAuthenticated } = useAuth();

  // Helper function to convert SplitCollaboratorInput to Collaborator for validation
  const getCollaboratorForValidation = (splitCollab: SplitCollaboratorInput): Collaborator => {
    // If this is an existing collaborator, get the name from the collaborator database
    let collaboratorName = splitCollab.name || '';
    
    // If we have a collaboratorId but no name, this shouldn't happen with our fixes
    // but as a fallback, we could look up the name from availableCollaborators
    if (splitCollab.collaboratorId && !collaboratorName) {
      // For now, we'll use a placeholder since we don't have access to availableCollaborators here
      collaboratorName = 'Selected Collaborator';
    }
    
    return {
      name: collaboratorName,
      role: splitCollab.role,
      share: splitCollab.share,
      email: splitCollab.email || '',
      ipiNumber: splitCollab.ipiNumber || ''
    };
  };

  const collaboratorsForValidation = collaborators.map(getCollaboratorForValidation);
  const validation = useSplitValidation(songTitle, collaboratorsForValidation);


  const totalShare = collaborators.reduce((sum, collaborator) => sum + collaborator.share, 0);
  
  // For managers, require account selection via header picker
  const needsAccountSelection = isManager && !selectedAccount && artistAccounts.length > 0;
  const hasNoArtistAccounts = isManager && artistAccounts.length === 0;
  
  const canSubmit = validation.isValid && !isSubmitting && !needsAccountSelection && !hasNoArtistAccounts;

  // Define collaborator limit - all users can have up to 10 collaborators
  const maxCollaborators = 10;

  // Handler for creating artist account
  const handleCreateArtistAccount = async () => {
    if (!newAccountName.trim()) return;
    
    setIsCreatingAccount(true);
    try {
      await createAccount({
        name: newAccountName.trim(),
        type: 'artist',
      });
      await refreshAccounts();
      setShowCreateAccountModal(false);
      setNewAccountName('');
      showToast('Artist account created successfully!', 'success');
    } catch (err) {
      console.error('Failed to create account:', err);
      showToast('Failed to create account. Please try again.', 'error');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const addCollaborator = () => {
    if (collaborators.length < maxCollaborators) {
      setCollaborators([...collaborators, { name: '', role: 'composer', share: 0, email: '', ipiNumber: '', pro: '' }]);
    }
  };

  const removeCollaborator = (index: number) => {
    if (collaborators.length > 1) {
      setCollaborators(collaborators.filter((_, i) => i !== index));
    }
  };

  const handleCollaboratorChange = (index: number, newCollaborator: SplitCollaboratorInput) => {
    setCollaborators(collaborators.map((collaborator, i) => 
      i === index ? newCollaborator : collaborator
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    
    // Validate account selection for managers
    if (isManager && !selectedAccount) {
      setError('Please select an artist account from the header to assign this work to');
      return;
    }

    // Validate emails when sending invites
    if (sendInvites) {
      const missingEmails = collaborators.filter(c => !c.email?.trim());
      if (missingEmails.length > 0) {
        setError('All collaborators need email addresses when sending invites');
        return;
      }
      
      // Check for duplicate emails when sending invites
      const emails = collaborators.map(c => c.email?.trim().toLowerCase()).filter(email => email);
      const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
      if (duplicateEmails.length > 0) {
        setError('Duplicate email addresses found. Each collaborator must have a unique email address.');
        return;
      }
    }

    // No validation needed for agreeOnBehalf - it's manager-only

    setIsSubmitting(true);
    setError(null);

    try {
      const splitData: SplitData = {
        songTitle: songTitle.trim(),
        duration: getDurationInSeconds(),
        songCreationDate: songCreationDate || undefined,
        alternateTitles: alternateTitles.length > 0 ? alternateTitles : undefined,
        collaborators: collaborators.map(c => ({
          ...c,
          name: c.name?.trim() || '',
          email: c.email?.trim(),
          ipiNumber: c.ipiNumber?.trim(),
          pro: c.pro?.trim()
        })),
        sendInvites,
        agreeOnBehalf: isManager ? agreeOnBehalf : false,
        accountId: selectedAccount?.id || undefined
      };

      const result = await createSplit(splitData);
      setSubmitResult(result);
      
      // Scroll to top to show the success summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSongTitle('');
    setDurationMinutes('');
    setDurationSeconds('');
    setSongCreationDate('');
    setAlternateTitles([]);
    setNewAlternateTitle('');
    setCollaborators([{ name: '', role: 'composer', share: 0, email: '', ipiNumber: '', pro: '' }]);
    setSubmitResult(null);
    setError(null);
    setSendInvites(false);
    setAgreeOnBehalf(false);
    // Account selection is now handled via header picker, no need to reset
  };

  const handleDownloadPDF = async () => {
    if (!submitResult) return;

    // Check if user is authenticated for PDF download
    if (!isAuthenticated) {
      setUpgradeFeature('PDF download');
      setShowProUpgrade(true);
      return;
    }

    setIsDownloadingPDF(true);
    try {
      const pdfUrl = await downloadSplitPDF(submitResult.id);
      
      // Open the S3 signed URL in a new tab to download
      window.open(pdfUrl, '_blank');
      
      showToast('PDF download started!', 'success');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      showToast('Failed to download PDF', 'error');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (submitResult) {
    return (
      <div className="min-h-screen bg-[#18181B]">
        {ToastComponent}
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <div className="card p-4 sm:p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-success mb-2">Split Created Successfully!</h2>
              <p className="text-sm sm:text-base text-content-secondary">Your songwriting split has been saved.</p>
            </div>

            <div className="card border-green-500/20 p-3 sm:p-4 mb-6">
              <h3 className="font-semibold text-base sm:text-lg mb-3 text-content">{songTitle}</h3>
              
              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {collaborators.map((collaborator, index) => (
                  <div key={index} className="bg-surface-tertiary/50 rounded-lg p-3 border border-border-subtle/50">
                    <div className="font-medium text-content mb-2">{collaborator.name}</div>
                    <div className="flex justify-between text-sm">
                      <span className="text-content-muted">Role</span>
                      <span className="text-content-secondary capitalize">{collaborator.role}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-content-muted">Share</span>
                      <span className="text-content">{collaborator.share}%</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle">
                      <th className="text-left py-2 text-content-secondary">Name</th>
                      <th className="text-left py-2 text-content-secondary">Role</th>
                      <th className="text-left py-2 text-content-secondary">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collaborators.map((collaborator, index) => (
                      <tr key={index} className="border-b border-border-subtle/50">
                        <td className="py-2 text-content">{collaborator.name}</td>
                        <td className="py-2 text-content-secondary capitalize">{collaborator.role}</td>
                        <td className="py-2 text-content">{collaborator.share}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-center space-y-3">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <PrimaryButton
                  onClick={handleDownloadPDF}
                  disabled={isDownloadingPDF}
                  loading={isDownloadingPDF}
                  className="w-full sm:w-auto"
                >
                  {isAuthenticated ? 'Download PDF' : 'Sign Up for PDF'}
                </PrimaryButton>
                
                <SecondaryButton onClick={resetForm} className="w-full sm:w-auto">
                  Create Another Split
                </SecondaryButton>
              </div>
              
              {!isAuthenticated && (
                <div className="mt-4 card border-blue-500/40 p-3">
                  <p className="text-sm text-blue-300">
                    <strong>Want more features?</strong> Sign up for a free account to download PDF agreements, send email invites, and collect confirmations!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18181B]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {ToastComponent}
        
        {/* Header Section */}
        <div className="bg-surface-secondary border border-border-subtle rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Create Split</h1>
              <p className="text-sm sm:text-base text-[#6B7280]">Create a new music split record</p>
            </div>
          </div>
        </div>

        {/* Create Artist Account Prompt - shown when manager has no artist accounts */}
        {isManager && hasNoArtistAccounts && (
          <div className="bg-surface-secondary border border-border-subtle rounded-lg p-8 mb-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#14B8A6] mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-content mb-2">Create an Artist Account</h2>
            <p className="text-content-secondary mb-6 max-w-md mx-auto">
              You need at least one artist account to create splits. Artist accounts represent the creators whose works you manage.
            </p>
            <PrimaryButton
              type="button"
              onClick={() => setShowCreateAccountModal(true)}
            >
              Create Artist Account
            </PrimaryButton>
          </div>
        )}

        {/* Form Section - blurred when no artist accounts */}
        <div className={`bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden shadow-card p-4 sm:p-6 ${hasNoArtistAccounts ? 'opacity-30 pointer-events-none blur-sm' : ''}`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Manager account selection notice */}
            {isManager && needsAccountSelection && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-300 text-sm flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Please select an artist account from the header to assign this work to.
                </p>
              </div>
            )}

            {/* Song Details Section */}
            <div className="bg-[#1F1F23] border border-[#3F3F46] rounded-xl p-4">
              {/* Section Header */}
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <span className="text-sm font-medium text-accent">Song Details</span>
              </div>

              {/* Song Title */}
              <div className="mb-4">
                <label htmlFor="songTitle" className="block text-sm font-medium text-content-secondary mb-2">
                  Song Title <span className="text-state-error">*</span>
                </label>
                <input
                  type="text"
                  id="songTitle"
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                  placeholder="Enter song title"
                  required
                />
              </div>

              {/* Date Written - full width on mobile */}
              <div className="mb-4">
                <label htmlFor="songCreationDate" className="block text-sm font-medium text-content-secondary mb-2">
                  Date Written
                </label>
                <input
                  type="date"
                  id="songCreationDate"
                  value={songCreationDate}
                  onChange={(e) => setSongCreationDate(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                />
              </div>

              {/* Duration - own row to prevent mobile overflow */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-content-secondary mb-2">
                  Duration
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className="w-20 px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-center text-sm"
                    placeholder="0"
                  />
                  <span className="text-content-muted text-sm">min</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={durationSeconds}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setDurationSeconds(val > 59 ? '59' : e.target.value);
                    }}
                    className="w-20 px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-center text-sm"
                    placeholder="0"
                  />
                  <span className="text-content-muted text-sm">sec</span>
                </div>
              </div>

              {/* Alternate Titles - always show input */}
              <div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {alternateTitles.map((title, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-accent/20 border border-border-subtle rounded text-sm text-content-secondary"
                    >
                      {title}
                      <button
                        type="button"
                        onClick={() => removeAlternateTitle(index)}
                        className="text-content-muted hover:text-state-error ml-1"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {alternateTitles.length < 10 && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAlternateTitle}
                      onChange={(e) => setNewAlternateTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addAlternateTitle();
                        }
                      }}
                      className="flex-1 px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                      placeholder="+ Add alternate title"
                    />
                    {newAlternateTitle.trim() && (
                      <button
                        type="button"
                        onClick={addAlternateTitle}
                        className="px-3 py-2 bg-accent hover:bg-accent-hover text-content rounded-md transition-colors text-sm"
                      >
                        Add
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Collaborators */}
            <div className="bg-[#1F1F23] border border-[#3F3F46] rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-sm font-medium text-accent">Collaborators</span>
                </div>
                <span className="text-xs text-content-muted">Up to {maxCollaborators}</span>
              </div>

              {collaborators.map((collaborator, index) => {
                // Get all selected collaborator IDs except for the current one
                const selectedCollaboratorIds = collaborators
                  .filter((_, i) => i !== index) // Exclude current collaborator
                  .map(c => c.collaboratorId)
                  .filter((id): id is string => !!id); // Type-safe filter for defined IDs
                
                return (
                  <CollaboratorSelector
                    key={index}
                    value={collaborator}
                    onChange={(updatedCollaborator) => handleCollaboratorChange(index, updatedCollaborator)}
                    onRemove={() => removeCollaborator(index)}
                    index={index}
                    canRemove={collaborators.length > 1}
                    selectedCollaboratorIds={selectedCollaboratorIds}
                    accountId={selectedAccount?.id}
                  />
                );
              })}

              {/* Add Collaborator Button */}
              {collaborators.length < maxCollaborators && (
                <button
                  type="button"
                  onClick={addCollaborator}
                  className="w-full mt-2 py-3 border-2 border-dashed border-border-subtle rounded-lg text-content-muted hover:text-content hover:border-accent transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Another Collaborator
                </button>
              )}

              {/* Total Share Display */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-subtle">
                <span className="text-sm font-medium text-content">Total Share</span>
                <span className={`text-2xl font-bold ${totalShare === 100 ? 'text-accent' : 'text-state-error'}`}>
                  {totalShare}%
                </span>
              </div>
            </div>

            {/* Collaboration Options */}
            <div className="bg-[#1F1F23] border border-[#3F3F46] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-accent">Collaboration Options</span>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="sendInvites"
                    checked={sendInvites}
                    onChange={(e) => {
                      setSendInvites(e.target.checked);
                    }}
                    className="mt-1 h-4 w-4 text-pink-500 border-border-subtle rounded focus:ring-pink-500 bg-brand-navy"
                  />
                  <div className="flex-1">
                    <label htmlFor="sendInvites" className="text-sm font-medium text-content cursor-pointer">
                      Send email invites to collaborators
                    </label>
                    <p className="text-xs text-content-muted mt-1">
                      Requires all collaborators to have email addresses. Invites sent immediately upon split creation.
                    </p>
                  </div>
                </div>

                {isManager && (
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="agreeOnBehalf"
                      checked={agreeOnBehalf}
                      onChange={(e) => setAgreeOnBehalf(e.target.checked)}
                      className="mt-1 h-4 w-4 text-pink-500 border-border-subtle rounded focus:ring-pink-500 bg-brand-navy"
                    />
                    <div className="flex-1">
                      <label htmlFor="agreeOnBehalf" className="text-sm font-medium text-content cursor-pointer">
                        Agree on behalf of all collaborators
                      </label>
                      <p className="text-xs text-content-muted mt-1">
                        As a manager, you can confirm this work on behalf of all collaborators, marking it as confirmed immediately.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Validation Messages - compact inline style */}
            {(validation.errors.length > 0 || validation.warnings.length > 0) && (
              <div className="space-y-1">
                {validation.errors.map((errorMsg, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-red-400">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {errorMsg}
                  </div>
                ))}
                {validation.warnings.map((warning, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-yellow-400">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {warning}
                  </div>
                ))}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-surface-secondary border border-red-500/40 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-state-error flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-state-error font-medium">Error Creating Split</h4>
                    <p className="text-red-300 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <PrimaryButton
                type="submit"
                disabled={!canSubmit}
                loading={isSubmitting}
                className="w-full py-4 text-lg"
              >
                Create Split
              </PrimaryButton>
            </div>
          </form>
        </div>
        
        {/* Pro Upgrade Modal */}
        {showProUpgrade && (
          <ProUpgrade
            feature={upgradeFeature}
            onClose={() => setShowProUpgrade(false)}
          />
        )}

        {/* Create Artist Account Modal */}
        {showCreateAccountModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-secondary border border-border-subtle rounded-lg p-6 max-w-md w-full">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#14B8A6] mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-content mb-2">Create Artist Account</h2>
                <p className="text-content-secondary text-sm">
                  Enter the name for the artist account. This is the artist whose works you'll be managing.
                </p>
              </div>
              
              <div className="mb-6">
                <label htmlFor="newAccountName" className="block text-sm font-medium text-content-secondary mb-2">
                  Artist Name *
                </label>
                <input
                  type="text"
                  id="newAccountName"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Enter artist name"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setShowCreateAccountModal(false);
                    setNewAccountName('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="button"
                  onClick={handleCreateArtistAccount}
                  disabled={!newAccountName.trim() || isCreatingAccount}
                  loading={isCreatingAccount}
                  className="flex-1"
                >
                  Create Account
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitCalculator;
